import { DateTime } from 'luxon'

/** Format and ISO date string to the given Luxon format, or 'HH:mm' by default */
export const formatTime = (date: string, format = 'HH:mm') =>
  DateTime.fromISO(date).toFormat(format)

/**
 * Format an ISO date to the given format, using the city name given in zoneId to denote the time zone
 * @example
 * // Returns '13:45 EDT'
 * formatTimeWithZoneAbbr('2019-01-03T13:45:53.530', 'EDT')
 * @param date a string containing an ISO datetime
 * @param zoneAbbr 
 * @param format a valid Luxon format string, or undefined to use the default 'HH:mm' (24 hour time with hours padded to 2 digits)
 */
export const formatTimeWithZoneAbbr = (
  date: string,
  zoneAbbr?: string,
  format = 'HH:mm'
) => {
  const formattedTime = formatTime(date, format)
  if (!zoneAbbr) return formattedTime

  return `${formattedTime} ${zoneAbbr}`
}

/**
 * Formats event time display with consistent timezone handling
 * Used by EventListing and EventTeaser components
 * 
 * @param startDate - Event start date ISO string
 * @param endDate - Event end date ISO string (optional)
 * @param timeZoneAbbreviation - Timezone abbreviation (e.g., 'EST', 'PST')
 * @returns Formatted time string with timezone
 * 
 * @example
 * // With start and end date
 * formatEventTimeDisplay('2024-01-15T14:00:00Z', '2024-01-15T16:00:00Z', 'EST')
 * // Returns: "2:00 PM - 4:00 PM EST"
 * 
 * // With only start date
 * formatEventTimeDisplay('2024-01-15T14:00:00Z', null, 'PST')
 * // Returns: "2:00 PM PST"
 */
export const formatEventTimeDisplay = (
  startDate: string,
  endDate?: string | null, 
  timeZoneAbbreviation?: string | null
): string => {
  if (!startDate) return ''

  const startTime = formatTime(startDate)
  const endTime = endDate
    ? formatTimeWithZoneAbbr(endDate, timeZoneAbbreviation)
    : ''

  if (endTime) {
    return `${startTime} - ${endTime}`
  }

  if (timeZoneAbbreviation) {
    return `${startTime} ${timeZoneAbbreviation}`
  }

  return startTime
}

